/**
 * Shared alert type configuration.
 * Used in AlertsPage (display) and VehicleDetail (toggle settings).
 */

export const ALERT_CONFIG = {
  ENGINE_ON: {
    icon: "power",
    color: "text-success",
    bg: "bg-success/10",
    label: "Motor encendido",
  },
  ENGINE_OFF: {
    icon: "power_off",
    color: "text-text-dim",
    bg: "bg-text-dim/10",
    label: "Motor apagado",
  },
  GEOFENCE_ENTER: {
    icon: "login",
    color: "text-success",
    bg: "bg-success/10",
    label: "Entró a geocerca",
  },
  GEOFENCE_EXIT: {
    icon: "logout",
    color: "text-warning",
    bg: "bg-warning/10",
    label: "Salió de geocerca",
  },
  GEOFENCE_DWELL: {
    icon: "timer",
    color: "text-danger",
    bg: "bg-danger/10",
    label: "Permanencia en zona",
  },
  BATTERY_CUT: {
    icon: "battery_alert",
    color: "text-danger",
    bg: "bg-danger/10",
    label: "Batería desconectada",
  },
  POWER_RESTORED: {
    icon: "battery_charging_full",
    color: "text-success",
    bg: "bg-success/10",
    label: "Batería conectada",
  },
  // Corte de batería abierto y el vehículo se mueve (backend
  // services/theftSuspicion.js). Una por episodio de corte.
  THEFT_SUSPECTED: {
    icon: "gpp_maybe",
    color: "text-danger",
    bg: "bg-danger/10",
    label: "Posible robo",
  },
  LOW_BATTERY: {
    icon: "battery_2_bar",
    color: "text-warning",
    bg: "bg-warning/10",
    label: "Batería baja",
  },
  OVERSPEED: {
    icon: "speed",
    color: "text-danger",
    bg: "bg-danger/10",
    label: "Exceso de velocidad",
  },
  SOS: {
    icon: "sos",
    color: "text-danger",
    bg: "bg-danger/10",
    label: "Botón de pánico",
  },
  VIBRATION: {
    icon: "vibration",
    color: "text-warning",
    bg: "bg-warning/10",
    label: "Vibración",
  },
  PARKED: {
    icon: "local_parking",
    color: "text-warning",
    bg: "bg-warning/10",
    label: "Estacionado",
  },
  ACCIDENT: {
    icon: "car_crash",
    color: "text-danger",
    bg: "bg-danger/10",
    label: "Accidente detectado",
  },
  FALL_DOWN: {
    icon: "person_alert",
    color: "text-danger",
    bg: "bg-danger/10",
    label: "Caída detectada",
  },
  TAMPERING: {
    icon: "build_circle",
    color: "text-danger",
    bg: "bg-danger/10",
    label: "Manipulación del dispositivo",
  },
  REMOVING: {
    icon: "power_input",
    color: "text-danger",
    bg: "bg-danger/10",
    label: "Dispositivo desinstalado",
  },
  JAMMING: {
    icon: "signal_disconnected",
    color: "text-warning",
    bg: "bg-warning/10",
    label: "Interferencia de señal",
  },
  COMMAND_RESULT: {
    icon: "task_alt",
    // Mismo neutro que ENGINE_OFF, que también es INFO. NO inventar un
    // `bg-surface-muted`: la escala de superficies es surface-1/2/3 y un token
    // inexistente no emite CSS — el chip queda sin fondo y ni el guard de
    // tokens ni los tests lo ven (el guard solo persigue hex y paletas default).
    color: "text-text-dim",
    bg: "bg-text-dim/10",
    label: "Comando confirmado",
  },
  POWER_OFF: {
    icon: "power_off",
    color: "text-danger",
    bg: "bg-danger/10",
    // "Apagado de energía" se leía como el corte de batería del vehículo, que
    // es BATTERY_CUT ("Batería desconectada"). Esto es el equipo apagándose.
    label: "Equipo apagado",
  },
  DEVICE_OFFLINE: {
    icon: "cloud_off",
    color: "text-danger",
    bg: "bg-danger/10",
    label: "Dispositivo desconectado",
  },
  // RETIRADA: ya nadie emite GPS_STALE. Un GPS que no reporta posiciones es el
  // estado normal de un vehículo quieto, y avisarlo era ruido que además
  // contradecía al panel del vehículo ("Última señal: hace 2 minutos"). La
  // única alerta de conectividad es DEVICE_OFFLINE. La entrada se conserva para
  // poder RENDERIZAR las filas históricas de `alerts`; fuera de
  // ALL_NOTIFIABLE_ALERT_TYPES a propósito, para que no se pueda volver a
  // suscribir a ella.
  GPS_STALE: {
    icon: "satellite_alt",
    color: "text-warning",
    bg: "bg-warning/10",
    label: "Sin posición GPS",
  },
  DOOR: {
    icon: "door_open",
    color: "text-warning",
    bg: "bg-warning/10",
    label: "Apertura de puerta",
  },
};

// Types whose enable/disable is configured per-device via /api/alert-settings.
// Subset of ALL_NOTIFIABLE_ALERT_TYPES — only these render a device list in
// the alerts-config drilldown.
export const CONFIGURABLE_ALERT_TYPES = [
  "ENGINE_ON",
  "ENGINE_OFF",
  "GEOFENCE_ENTER",
  "GEOFENCE_EXIT",
  "BATTERY_CUT",
  "POWER_RESTORED",
  "PARKED",
  "LOW_BATTERY",
  "OVERSPEED",
  "SOS",
  "VIBRATION",
];

// Every alert type the user may want to receive notifications for. Superset of
// CONFIGURABLE_ALERT_TYPES — types here that are NOT in CONFIGURABLE_ALERT_TYPES
// are channel-only in the UI (no per-device toggle, just web_push / telegram).
// Order is the rendering order in the alerts-config card list.
export const ALL_NOTIFIABLE_ALERT_TYPES = [
  "SOS",
  "THEFT_SUSPECTED",
  "ACCIDENT",
  "FALL_DOWN",
  "TAMPERING",
  "REMOVING",
  "JAMMING",
  "GEOFENCE_ENTER",
  "GEOFENCE_EXIT",
  "GEOFENCE_DWELL",
  "BATTERY_CUT",
  "POWER_OFF",
  "POWER_RESTORED",
  "LOW_BATTERY",
  "DEVICE_OFFLINE",
  "OVERSPEED",
  "VIBRATION",
  "PARKED",
  "ENGINE_ON",
  "ENGINE_OFF",
  "DOOR",
];

// Set lookup helper for "does this type have a per-device toggle list?"
export const CONFIGURABLE_ALERT_TYPE_SET = new Set(CONFIGURABLE_ALERT_TYPES);

/**
 * Severidad por tipo — espejo de `DEFAULT_POLICY` en
 * `backend/src/notifications/policy.js`.
 *
 * Se duplica acá porque el evento `alert` del WebSocket NO trae el nivel (ver
 * `backend/src/ws/server.js`): la locución por voz filtra por severidad y
 * necesita clasificar la alerta en el mismo instante en que llega, sin ida y
 * vuelta al API. Si cambia una severidad en el backend, cambiarla también acá.
 */
export const ALERT_LEVELS = {
  SOS: "CRITICAL",
  THEFT_SUSPECTED: "CRITICAL",
  ACCIDENT: "CRITICAL",
  FALL_DOWN: "CRITICAL",
  TAMPERING: "CRITICAL",
  REMOVING: "CRITICAL",
  JAMMING: "CRITICAL",
  GEOFENCE_ENTER: "CRITICAL",
  GEOFENCE_EXIT: "CRITICAL",
  GEOFENCE_DWELL: "CRITICAL",
  BATTERY_CUT: "CRITICAL",
  LOW_BATTERY: "CRITICAL",
  POWER_OFF: "HIGH",
  DEVICE_OFFLINE: "HIGH",
  GPS_STALE: "HIGH",
  OVERSPEED: "HIGH",
  VIBRATION: "HIGH",
  PARKED: "HIGH",
  DOOR: "HIGH",
  POWER_RESTORED: "INFO",
  ENGINE_ON: "INFO",
  ENGINE_OFF: "INFO",
};

/** Orden de severidad, de menor a mayor. Índice = umbral comparable. */
export const LEVEL_ORDER = ["INFO", "HIGH", "CRITICAL"];

/**
 * Severidad de un tipo que este mapa todavía no conoce.
 *
 * HIGH y no INFO, y la diferencia con el backend es DELIBERADA: allá
 * `resolvePolicy` cae a INFO con `channels: []`, que decide si se ENVÍA una
 * notificación. Acá se decide si se LOCUTA una alerta que el WebSocket ya
 * entregó — y el broadcast no filtra por política, así que un tipo nuevo llega
 * igual. Con INFO caería por debajo del umbral por defecto (HIGH) y el operador
 * no oiría absolutamente nada de un evento que el backend consideró digno de
 * alerta. HIGH lo deja pasar al fallback genérico de `alertSpeech`, que al menos
 * dice de qué vehículo se trata.
 */
export const UNKNOWN_ALERT_LEVEL = "HIGH";

export function levelOf(alertType) {
  return ALERT_LEVELS[alertType] ?? UNKNOWN_ALERT_LEVEL;
}

/**
 * ¿Este tipo es urgente de fábrica?
 *
 * "Urgente" es el concepto que el usuario configura en Control → Alertas: la
 * alerta suena aunque el teléfono esté bloqueado, en silencio o en No molestar,
 * y se locuta por el canal de alarma. Lo que NO es urgente entra a la bandeja
 * sin interrumpir.
 *
 * Espejo de `defaultUrgent` en `backend/src/notifications/policy.js`: urgente
 * de fábrica ⇔ nivel CRITICAL. La flota puede cambiarlo por tipo, y esa
 * elección la sirve `GET /api/alert-settings/urgency` (hook
 * `useAlertUrgency`). Este default es lo que se muestra mientras esa respuesta
 * no llegó, y lo único que hay para un tipo que el backend todavía no conoce.
 */
export function isUrgentByDefault(alertType) {
  return levelOf(alertType) === "CRITICAL";
}

// Functional grouping for the alerts-config card list. The first category is
// expanded by default; the rest collapse to keep the list scannable. Order is
// rendering order. Each `types` array MUST be a subset of
// ALL_NOTIFIABLE_ALERT_TYPES (or render will silently drop unknown keys).
export const ALERT_TYPE_CATEGORIES = [
  {
    key: "security",
    label: "Seguridad",
    icon: "shield_person",
    types: ["SOS", "ACCIDENT", "FALL_DOWN"],
  },
  {
    key: "tampering",
    label: "Manipulación",
    icon: "build_circle",
    types: ["TAMPERING", "REMOVING", "JAMMING"],
  },
  {
    key: "geofences",
    label: "Geocercas",
    icon: "fence",
    types: ["GEOFENCE_ENTER", "GEOFENCE_EXIT", "GEOFENCE_DWELL"],
  },
  {
    key: "power",
    label: "Energía y batería",
    icon: "bolt",
    types: ["BATTERY_CUT", "POWER_OFF", "POWER_RESTORED", "LOW_BATTERY"],
  },
  {
    key: "driving",
    label: "Conducción",
    icon: "speed",
    types: ["OVERSPEED", "VIBRATION", "PARKED"],
  },
  {
    key: "device",
    label: "Motor y dispositivo",
    icon: "directions_car",
    types: ["ENGINE_ON", "ENGINE_OFF", "DEVICE_OFFLINE", "DOOR"],
  },
];

/**
 * Typed threshold config per alert_type — mirror of backend THRESHOLD_CONFIG.
 *
 *   unit:      'minutes' | 'percent' | 'kmh' (server-side persisted)
 *   default/min/max: integers in the unit's native scale
 *   displayUnit: short label shown next to the input (the UI converts when needed)
 *   displayMin/displayMax/displayDefault: scaled values for the input control
 *     (PARKED stores minutes but displays hours)
 *   label: human-readable label for the input
 *
 * Types absent from this map are toggle-only (no threshold UI).
 */
export const THRESHOLD_CONFIG = {
  PARKED: {
    unit: "minutes",
    default: 360,
    min: 30,
    max: 4320,
    displayUnit: "h",
    displayDefault: 6,
    displayMin: 1,
    displayMax: 72,
    label: "Umbral de estacionamiento",
    options: [1, 2, 4, 6, 8, 12, 24, 48, 72],
    toDisplay: (v) => Math.round(v / 60),
    fromDisplay: (v) => v * 60,
  },
  LOW_BATTERY: {
    unit: "percent",
    default: 20,
    min: 5,
    max: 50,
    displayUnit: "%",
    displayDefault: 20,
    displayMin: 5,
    displayMax: 50,
    label: "Umbral de batería baja",
    options: [5, 10, 15, 20, 25, 30, 40, 50],
    toDisplay: (v) => v,
    fromDisplay: (v) => v,
  },
  OVERSPEED: {
    unit: "kmh",
    default: 80,
    min: 40,
    max: 200,
    displayUnit: "km/h",
    displayDefault: 80,
    displayMin: 40,
    displayMax: 200,
    label: "Umbral de velocidad",
    options: [40, 60, 80, 100, 120, 140, 160, 200],
    toDisplay: (v) => v,
    fromDisplay: (v) => v,
  },
};
